import { StatefulButton } from '@ddc-webplatform/cmsc-components';
import { launchdarkly } from '@ddc-webplatform/cmsc-modules';
import { SideDrawerLayout } from 'layouts';
import { Close, ExpandMore, KeyboardArrowLeft } from '@mui/icons-material';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	Typography
} from '@mui/material';
import {
	ThemeProvider as MuiThemeProvider,
	StyledEngineProvider
} from '@mui/material/styles';
import PageDetails from 'components/PageDetails/PageDetails';
import SitemapTreeView from 'components/SitemapTreeView/SitemapTreeView';
import { transformSitemapToTreeView } from 'helpers/transformSitemapToTreeView';
import { useSitemapData } from 'hooks/useSitemapData/useSitemapData';
import { PageData, SitemapCollectionData, TreeViewNode } from 'models';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import theme from 'theme/theme';
import './App.scss';
import { dialog as dialogColors } from 'theme/colors';
import {
	DEFAULT_MAX_BATCH_SIZE,
	LD_FLAG_BATCH_DELETE_SIZE_LIMIT
} from 'constants/ui';
import { SitemapApi } from 'api';
import ErrorBoundary from 'components/ErrorBoundary';
import BatchEditPageDetails from 'components/BatchEditPageDetails';
import { BatchSizeLimitDialog } from 'components/BatchSizeLimitDialog';
import { ConfirmDialog } from 'components/ConfirmDialog';
import { toDateString } from 'helpers/utils';
import { fireCloseEvent } from '../helpers/app';
import { exportSitemapAsCSV } from '../helpers/exportSitemap';
import { fireTrackableEvent } from '../helpers/analytics';
import { TRACKING_EVENT } from '../constants/trackingEvents';

const footerStyles = {
	padding: '4px 8px',
	gap: '8px',
	opacity: 1,
	alignItems: 'center',
	borderTop: (t: any) => `1px solid ${t.palette.grey[600]}`,
	backgroundColor: (t: any) => t.palette.background.default,
	flexShrink: 0
};

const AppContainer = () => {
	const [open, setOpen] = useState(true);
	const [isManageMode, setIsManageMode] = useState(false);
	const [selectedItems, setSelectedItems] = useState<string[]>([]);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [isViewMoreExpanded, setIsViewMoreExpanded] = useState(false);
	const [selectedPage, setSelectedPage] = useState<PageData | null>(null);
	const [isDetailsOpen, setIsDetailsOpen] = useState(false);
	const [isEntryLoading, setIsEntryLoading] = useState(false);
	const [entryLoadError, setEntryLoadError] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isDeleteLimitDialogOpen, setIsDeleteLimitDialogOpen] = useState(false);
	const selectedPathRef = React.useRef<string | null>(null);
	const contentReadyFiredRef = React.useRef(false);
	const pageDetailsHasChangesRef = React.useRef(false);
	const isDetailsOpenRef = React.useRef(false);
	const batchEditHasChangesRef = React.useRef(false);
	const isBatchEditOpenRef = React.useRef(false);
	const parentNodeIdsRef = React.useRef<Set<string>>(new Set());
	const pendingExternalActionRef = React.useRef<(() => void) | null>(null);
	const [isExternalDiscardOpen, setIsExternalDiscardOpen] = useState(false);
	const [isExternalBatchDiscardOpen, setIsExternalBatchDiscardOpen] = useState(false);

	const rawDeleteLimit = launchdarkly.useFlag(LD_FLAG_BATCH_DELETE_SIZE_LIMIT);
	const batchDeleteLimit =
		typeof rawDeleteLimit === 'number' ? rawDeleteLimit : DEFAULT_MAX_BATCH_SIZE;

	const {
		sitemapData,
		isLoading,
		error,
		invalidPages,
		navigateToPage,
		refetch
	} = useSitemapData();

	// Keep the isDetailsOpen ref in sync for use inside stable callbacks
	useEffect(() => {
		isDetailsOpenRef.current = isDetailsOpen;
	}, [isDetailsOpen]);

	// Guard helper: if PageDetails or BatchEditPageDetails has unsaved changes,
	// show confirm dialog; otherwise run the action immediately.
	const guardAction = useCallback((action: () => void) => {
		if (isDetailsOpenRef.current && pageDetailsHasChangesRef.current) {
			pendingExternalActionRef.current = action;
			setIsExternalDiscardOpen(true);
		} else if (isBatchEditOpenRef.current && batchEditHasChangesRef.current) {
			pendingExternalActionRef.current = action;
			setIsExternalBatchDiscardOpen(true);
		} else {
			action();
		}
	}, []);

	const handleExternalDiscardConfirm = useCallback(() => {
		setIsExternalDiscardOpen(false);
		const action = pendingExternalActionRef.current;
		pendingExternalActionRef.current = null;
		action?.();
	}, []);

	const handleExternalDiscardCancel = useCallback(() => {
		setIsExternalDiscardOpen(false);
		pendingExternalActionRef.current = null;
	}, []);

	const handleExternalBatchDiscardConfirm = useCallback(() => {
		setIsExternalBatchDiscardOpen(false);
		const action = pendingExternalActionRef.current;
		pendingExternalActionRef.current = null;
		action?.();
	}, []);

	const handleExternalBatchDiscardCancel = useCallback(() => {
		setIsExternalBatchDiscardOpen(false);
		pendingExternalActionRef.current = null;
	}, []);

	const handleClose = useCallback(() => {
		guardAction(() => {
			setOpen(false);
			setIsDetailsOpen(false);
			setSelectedItems([]);
		});
	}, [guardAction]);

	// Fire open event once on mount
	useEffect(() => {
		fireTrackableEvent(TRACKING_EVENT.OPEN_SITEMAP_EDITOR);
	}, []);

	// Notify the ExtJS host once the treeview data is ready to display —
	// used to fade out the skeleton loader panel. Fires only once.
	useEffect(() => {
		if (isLoading || error || !sitemapData?.tree || contentReadyFiredRef.current) return;
		const container = document.getElementById('cmsc-sitemap-editor-ct');
		if (container) {
			container.dispatchEvent(new CustomEvent('app-content-ready'));
			contentReadyFiredRef.current = true;
		}
	}, [isLoading, error, sitemapData?.tree]);

	// Listen for ExtJS-initiated close (e.g. click outside)
	useEffect(() => {
		const container = document.getElementById('cmsc-sitemap-editor-ct');
		if (!container) return undefined;
		const onExternalClose = () => handleClose();
		container.addEventListener('close-drawer', onExternalClose);
		return () => container.removeEventListener('close-drawer', onExternalClose);
	}, [handleClose]);

	// Transform sitemap data to TreeView format
	// No map params — pageAlias / pageId / source are on the DTO nodes
	const treeViewItems = useMemo(() => {
		if (!sitemapData?.tree) return [];
		return transformSitemapToTreeView(sitemapData.tree, invalidPages);
	}, [sitemapData?.tree, invalidPages]);

	// Load available page ID options once on mount
	const [pageIdOptions, setPageIdOptions] = useState<string[]>([]);
	useEffect(() => {
		SitemapApi.loadAvailablePages()
			.then(data => setPageIdOptions([...new Set<string>(data ?? [])]))
			.catch(err => console.error('Failed to load available pages:', err));
	}, []);

	// Toggle manage mode
	const handleManageModeToggle = useCallback(() => {
		const doToggle = () => {
			setIsManageMode(prev => {
				if (!prev) {
					// Entering batch edit mode — close the page detail drawer
					setIsDetailsOpen(false);
					fireTrackableEvent(TRACKING_EVENT.ENTER_MANAGE_MODE);
				}
				return !prev;
			});
			setSelectedItems([]); // Clear selection when toggling mode
		};
		guardAction(doToggle);
	}, [guardAction]);;

	// Handle selection change
	const handleSelectionChange = useCallback(
		(_event: React.SyntheticEvent, nodeIds: string[] | string) => {
			let normalized: string[];
			if (Array.isArray(nodeIds)) {
				normalized = nodeIds;
			} else {
				normalized = nodeIds ? [nodeIds] : [];
			}

			// Guard: if batch edit drawer has unsaved changes and the new selection
			// would close it (0 leaf items), show the discard dialog first.
			if (isBatchEditOpenRef.current && batchEditHasChangesRef.current) {
				const newLeafCount = normalized.filter(id => !parentNodeIdsRef.current.has(id)).length;
				if (newLeafCount === 0) {
					guardAction(() => setSelectedItems(normalized));
					return;
				}
			}

			setSelectedItems(normalized);
		},
		[guardAction]
	);

	// Handle leaf item click (for viewing details)
	const handleLeafItemClick = useCallback(
		async (
			event: React.SyntheticEvent | undefined,
			nodeId: string,
			nodeData?: TreeViewNode
		) => {
			event?.stopPropagation();
			// Resolve the clicked node (from callback data or tree search)
			let node: TreeViewNode | null | undefined = nodeData;
			if (!node) {
				const findItem = (
					items: TreeViewNode[],
					id: string
				): TreeViewNode | null => {
					const direct = items.find(item => item.id === id);
					if (direct) return direct;
					return items.reduce<TreeViewNode | null>(
						(acc, item) =>
							acc ?? (item.children ? findItem(item.children, id) : null),
						null
					);
				};
				node = findItem(treeViewItems, nodeId);
			}

			const path = node?.pagePath;
			if (!path) return;

			// Same page — just (re-)open, no guard needed
			if (path === selectedPathRef.current) {
				setSelectedItems([nodeId]);
				setIsDetailsOpen(true);
				return;
			}

			// Different page — guard against unsaved changes, then load
			const capturedNode = node;
			const doLoad = () => {
				setSelectedItems([nodeId]);
				selectedPathRef.current = path;
				setIsDetailsOpen(true);
				setEntryLoadError(false);
				setIsEntryLoading(true);
				(async () => {
					try {
						const entry = await SitemapApi.loadSitemapEntry(path);
						const entryPaths = entry?.pathsByLocale
							? Object.entries(entry.pathsByLocale).map(([locale, p]) => ({
									locale,
									path: p
							  }))
							: [];
						// Fall back to node.pageAlias (carried from the DTO)
						const pageAlias = entry?.alias || capturedNode?.pageAlias;
						const collectionData: Partial<SitemapCollectionData> = pageAlias
							? await SitemapApi.loadSitemapCollectionData(pageAlias).catch(
									(collErr: unknown) => {
										console.warn(
											'Failed to load sitemap collection data — showInHtmlSitemap/showInXmlSitemap will default to false:',
											collErr
										);
										return {} as Partial<SitemapCollectionData>;
									}
							  )
							: {};
						setSelectedPage({
							label: (entry?.path || path).replace(/^\//, ''),
							pagePath: entry?.path || path,
							localePaths: entryPaths,
							pageAlias: entry?.alias,
							pageId: entry?.pageId,
							noIndex: entry?.noIndex,
							noFollow: entry?.noFollow,
							showInHtmlSitemap: !!collectionData?.htmEntry,
							showInXmlSitemap: !!collectionData?.xmlEntry,
							addToSitemapOn: toDateString(collectionData?.xmlEntry?.startDate),
							removeFromSitemapOn: toDateString(collectionData?.xmlEntry?.endDate)
						});
					} catch (err) {
						console.error('Failed to load sitemap entry:', err);
						setEntryLoadError(true);
					} finally {
						setIsEntryLoading(false);
					}
				})();
			};

			guardAction(doLoad);
		},
		[treeViewItems, guardAction]
	);

	// Navigate to page when clicking a non-folder item
	const handleLeafNavigate = useCallback(
		(
			_event: React.SyntheticEvent | undefined,
			_nodeId: string,
			nodeData?: TreeViewNode
		) => {
			const path = nodeData?.pagePath;
			if (!path) return;
			fireTrackableEvent(TRACKING_EVENT.NAVIGATE_TO_PAGE);
			if (!path.match(/\.html$/) && !path.match(/\.htm$/)) {
				navigateToPage('');
			} else {
				navigateToPage(path);
			}
			handleClose();
		},
		[navigateToPage, handleClose]
	);

	// Handle closing Page Details drawer
	const handleCloseDetails = useCallback(() => {
		setIsDetailsOpen(false);
		selectedPathRef.current = null;
		setSelectedItems([]);
	}, []);

	// Handle closing Batch Edit drawer
	const handleCloseBatchEdit = useCallback(() => {
		setSelectedItems([]);
	}, []);

	// Handle successful save from Page Details:
	// clear the stale selection so the next open always re-fetches fresh data,
	// then reload the tree from the API
	// Handle successful save from Page Details
	const handleSaveDetails = useCallback(
		(_newPath: string) => {
			selectedPathRef.current = null;
			setSelectedPage(null);
			refetch();
		},
		[refetch]
	);

	// Handle successful save from Batch Edit:
	// clear the stale page ref so the next single-edit click always re-fetches
	const handleSaveBatchEdit = useCallback(() => {
		selectedPathRef.current = null;
		setSelectedPage(null);
		refetch();
	}, [refetch]);

	// Collect all parent node IDs (nodes that have children) recursively
	const parentNodeIds = useMemo(() => {
		const ids = new Set<string>();
		const collect = (nodes: TreeViewNode[]) => {
			nodes.forEach(node => {
				if (node.children && node.children.length > 0) {
					ids.add(node.id);
					collect(node.children);
				}
			});
		};
		collect(treeViewItems);
		return ids;
	}, [treeViewItems]);

	// Build a flat id → TreeViewNode map for O(1) node lookups (replaces separate
	// leafPathById / leafAliasById / leafPageIdById Maps — single pass over the tree)
	const nodeById = useMemo(() => {
		const map = new Map<string, TreeViewNode>();
		const collect = (nodes: TreeViewNode[]) =>
			nodes.forEach(n => {
				map.set(n.id, n);
				if (n.children) collect(n.children);
			});
		collect(treeViewItems);
		return map;
	}, [treeViewItems]);

	// Count only leaf (non-parent) selected items
	const selectedLeafCount = useMemo(
		() =>
			(Array.isArray(selectedItems) ? selectedItems : []).filter(
				(id: string) => !parentNodeIds.has(id)
			).length,
		[selectedItems, parentNodeIds]
	);

	// Determine if batch edit drawer should show
	const isBatchEditOpen = isManageMode && selectedLeafCount > 0;

	// Keep the isBatchEditOpen and parentNodeIds refs in sync for use inside stable callbacks
	useEffect(() => {
		isBatchEditOpenRef.current = isBatchEditOpen;
	}, [isBatchEditOpen]);

	useEffect(() => {
		parentNodeIdsRef.current = parentNodeIds;
	}, [parentNodeIds]);

	// Collect paths of selected leaf nodes for display in delete dialog
	const selectedLeafPaths = useMemo(() => {
		return (Array.isArray(selectedItems) ? selectedItems : [])
			.filter((id: string) => !parentNodeIds.has(id))
			.map((id: string) => {
				const n = nodeById.get(id);
				return n?.pagePath || n?.label || id;
			})
			.filter(Boolean);
	}, [selectedItems, parentNodeIds, nodeById]);

	// Collect aliases of selected leaf nodes for batch edit & delete
	const selectedLeafAliases = useMemo(() => {
		return (Array.isArray(selectedItems) ? selectedItems : [])
			.filter((id: string) => !parentNodeIds.has(id))
			.map((id: string) => nodeById.get(id)?.pageAlias ?? null)
			.filter(Boolean);
	}, [selectedItems, parentNodeIds, nodeById]);

	const selectedLeafAliasToPageIdMap = useMemo(() => {
		const result: Record<string, string> = {};
		(Array.isArray(selectedItems) ? selectedItems : [])
			.filter((id: string) => !parentNodeIds.has(id))
			.forEach((id: string) => {
				const n = nodeById.get(id);
				if (n?.pageAlias && n?.pageId) result[n.pageAlias] = n.pageId;
			});
		return result;
	}, [selectedItems, parentNodeIds, nodeById]);

	// Handle delete selected items
	const handleDelete = useCallback(() => {
		if (selectedLeafCount > batchDeleteLimit) {
			setIsDeleteLimitDialogOpen(true);
			return;
		}
		setIsViewMoreExpanded(false);
		setIsDeleteDialogOpen(true);
	}, [selectedLeafCount, batchDeleteLimit]);

	const handleDeleteConfirm = useCallback(async () => {
		if (selectedLeafAliases.length === 0) {
			console.error('No valid page aliases found for the selected items.');
			return;
		}

		setIsDeleting(true);
		try {
			const result = await SitemapApi.bulkDelete(selectedLeafAliases);

			if (result.failedCount > 0) {
				const failed = result.results
					.filter(r => r.status === 'FAILED')
					.map(r => r.pageAlias);
				console.warn('Bulk delete — partial failure:', failed);
			}

			const deletedCount = selectedLeafAliases.length - result.failedCount;
			fireTrackableEvent(TRACKING_EVENT.DELETE_PAGES, {
				eventResult: `${deletedCount} pages deleted`
			});
			setSelectedItems([]);
			setIsDeleteDialogOpen(false);
			selectedPathRef.current = null;
			setSelectedPage(null);
			refetch();
		} catch (err: unknown) {
			console.error(err instanceof Error ? err.message : 'Unexpected error');
		} finally {
			setIsDeleting(false);
		}
	}, [selectedLeafAliases, refetch]);

	const handleDeleteCancel = useCallback(() => {
		setIsDeleteDialogOpen(false);
	}, []);

	const handleCloseDeleteLimitDialog = useCallback(() => {
		setIsDeleteLimitDialogOpen(false);
	}, []);

	// Handle export to CSV
	const handleExport = useCallback(async () => {
		try {
			await exportSitemapAsCSV();
			fireTrackableEvent(TRACKING_EVENT.EXPORT_SITEMAP_CSV);
		} catch (exportError) {
			console.error('Failed to export sitemap:', exportError);
			// You could add a toast notification here if needed
		}
	}, []);

	// Loading state content
	// Error state content
	// Empty state content
	// (These are rendered inline below to avoid eagerly computing all branches)
	const hasData = sitemapData?.tree;

	const renderFooter = () => {
		if (isLoading) return undefined;
		if (!isManageMode) {
			return (
				<Box sx={footerStyles}>
					<StatefulButton
						startIcon={<InsertDriveFileOutlinedIcon />}
						label={<FormattedMessage id="SITEMAP_MANAGE_PAGES" />}
						variant="outlined"
						onClick={handleManageModeToggle}
						sx={{ padding: '5px 15px', height: 40 }}
					/>
				</Box>
			);
		}
		return (
			<Box
				sx={{
					...footerStyles,
					display: 'flex',
					justifyContent: 'space-between',
					gap: 1
				}}
			>
				<StatefulButton
					startIcon={<IosShareIcon />}
					label={<FormattedMessage id="EXPORT" />}
					variant="text"
					onClick={handleExport}
					sx={{ alignItems: 'normal' }}
				/>
				{selectedItems.length > 0 && (
					<StatefulButton
						label={
							<FormattedMessage
								id="SITEMAP_DELETE"
								values={{ count: selectedLeafCount }}
							/>
						}
						variant="contained"
						onClick={handleDelete}
						sx={{
							bgcolor: 'error.main',
							'&:hover': { bgcolor: 'error.dark' }
						}}
					/>
				)}
			</Box>
		);
	};

	return (
		<StyledEngineProvider injectFirst>
			<MuiThemeProvider theme={theme}>
				<SideDrawerLayout
					open={open}
					onClose={handleClose}
					title={isManageMode ? 'SITEMAP_MANAGE_PAGES' : 'SITEMAP_PAGES'}
					headerProps={{
						onCancel: handleClose,
						slotProps: {
							root: {
								sx: {
									bgcolor: '#212121',
									'& > .MuiStack-root': { flexWrap: 'wrap' }
								}
							},
							closeButton: {
								sx: { order: 2 }
							}
						},
						children: isManageMode ? (
							<Box
								sx={{
									flexBasis: '100%',
									order: 3,
									ml: '0 !important',
									mt: '8px !important'
								}}
							>
								<StatefulButton
									startIcon={<KeyboardArrowLeft />}
									label={<FormattedMessage id="BACK_TO_PAGES" />}
									variant="outlined"
									size="small"
									onClick={handleManageModeToggle}
								/>
							</Box>
						) : undefined
					}}
					hideBackdrop
					contentProps={{ sx: { p: 0 }, bgcolor: '#212121' }}
					SlideProps={{ onExited: () => fireCloseEvent() }}
					paperSx={{
						boxShadow: 'inset 4px 0px 8px 0px rgba(0, 0, 0, 0.5)'
					}}
					sx={{
						pointerEvents: isDetailsOpen || isBatchEditOpen ? 'none' : 'auto'
					}}
					footer={renderFooter()}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							overflow: 'hidden',
							fontFamily: theme.typography.fontFamily,
							height: '100%'
						}}
					>
						<ErrorBoundary>
							{isLoading && (
								<Box className="linearLoader">
									<LinearProgress color="inherit" sx={{ height: '6px' }} />
								</Box>
							)}
							{!isLoading && error && (
								<Box p={2}>
									<Typography color="error">
										<FormattedMessage
											id="ERROR_LOADING_SITEMAP"
											values={{ message: error.message }}
										/>
									</Typography>
								</Box>
							)}
							{!isLoading && !error && !hasData && (
								<Box p={2}>
									<Typography>
										<FormattedMessage id="NO_SITEMAP_DATA" />
									</Typography>
								</Box>
							)}
							{!isLoading && !error && hasData && (
								<SitemapTreeView
									items={treeViewItems}
									isManageMode={isManageMode}
									selectedItems={selectedItems}
									onSelectionChange={handleSelectionChange}
									onItemClick={isManageMode ? undefined : handleLeafNavigate}
									onEditClick={isManageMode ? undefined : handleLeafItemClick}
								/>
							)}
						</ErrorBoundary>
					</Box>
				</SideDrawerLayout>
				<PageDetails
					open={isDetailsOpen && !isBatchEditOpen}
					onClose={handleCloseDetails}
					onSave={handleSaveDetails}
					onHasChangesChange={hasChanges => {
						pageDetailsHasChangesRef.current = hasChanges;
					}}
					pageData={selectedPage}
					pageIdOptions={pageIdOptions}
					isLoading={isEntryLoading}
					loadError={entryLoadError}
				/>
				<ConfirmDialog
					open={isExternalDiscardOpen}
					onConfirm={handleExternalDiscardCancel}
					onCancel={handleExternalDiscardConfirm}
					onBackdropClose={handleExternalDiscardCancel}
					title={<FormattedMessage id="UNSAVED_CHANGES" />}
					content={<FormattedMessage id="UNSAVED_CHANGES_MESSAGE" />}
					confirmLabel={<FormattedMessage id="STAY" />}
					cancelLabel={<FormattedMessage id="DISCARD_CHANGES" />}
				/>
				<BatchEditPageDetails
					open={isBatchEditOpen}
					onClose={handleCloseBatchEdit}
					onHasChangesChange={hasChanges => {
						batchEditHasChangesRef.current = hasChanges;
					}}
					selectedCount={selectedLeafCount}
					pageAliases={selectedLeafAliases}
					aliasToPageIdMap={selectedLeafAliasToPageIdMap}
					onSave={handleSaveBatchEdit}
				/>
				<ConfirmDialog
					open={isExternalBatchDiscardOpen}
					onConfirm={handleExternalBatchDiscardCancel}
					onCancel={handleExternalBatchDiscardConfirm}
					onBackdropClose={handleExternalBatchDiscardCancel}
					title={<FormattedMessage id="UNSAVED_CHANGES" />}
					content={<FormattedMessage id="UNSAVED_CHANGES_MESSAGE" />}
					confirmLabel={<FormattedMessage id="STAY" />}
					cancelLabel={<FormattedMessage id="DISCARD_CHANGES" />}
				/>
				{isDeleteLimitDialogOpen && (
					<BatchSizeLimitDialog
						open={isDeleteLimitDialogOpen}
						onClose={handleCloseDeleteLimitDialog}
						title={<FormattedMessage id="BATCH_DELETE_LIMIT_TITLE" />}
						messages={[
							<FormattedMessage
								key="batch-delete-msg-1"
								id="BATCH_DELETE_LIMIT_MSG_1"
								values={{ batchDeleteLimit }}
							/>,
							<FormattedMessage
								key="batch-delete-msg-2"
								id="BATCH_DELETE_LIMIT_MSG_2"
								values={{ batchDeleteLimit }}
							/>,
							<FormattedMessage
								key="batch-delete-msg-3"
								id="BATCH_DELETE_LIMIT_MSG_3"
								values={{ batchDeleteLimit }}
							/>
						]}
						confirmLabel={
							<FormattedMessage id="BATCH_LIMIT_CONFIRM" />
						}
					/>
				)}
				{/* Delete confirmation dialog */}
				<Dialog
					open={isDeleteDialogOpen}
					onClose={handleDeleteCancel}
					maxWidth="sm"
					fullWidth
					sx={{
						zIndex: 99999,
						'& .MuiDialog-paper': { bgcolor: dialogColors.paper, minWidth: '810px' }
					}}
				>
					<DialogTitle sx={{ pr: 6 }}>
						<FormattedMessage
							id={
								selectedLeafPaths.length === 1 ? 'REMOVE_PAGE' : 'REMOVE_PAGES'
							}
						/>
						<IconButton
							aria-label="close"
							onClick={handleDeleteCancel}
							size="small"
							sx={{ position: 'absolute', right: 8, top: 8 }}
						>
							<Close fontSize="small" />
						</IconButton>
					</DialogTitle>
					<DialogContent>
						<Typography
							variant="body2"
							gutterBottom
							sx={{ marginBottom: '16px !important' }}
						>
							<FormattedMessage
								id={
									selectedLeafPaths.length === 1
										? 'DELETE_PAGE_WARNING'
										: 'DELETE_PAGES_WARNING'
								}
							/>
						</Typography>
						<Typography
							variant="body2"
							gutterBottom
							sx={{ marginBottom: '16px !important' }}
						>
							<FormattedMessage
								id={
									selectedLeafPaths.length === 1
										? 'DELETE_PAGE_CONFIRM'
										: 'DELETE_PAGES_CONFIRM'
								}
							/>
						</Typography>
						<List dense disablePadding>
							{(isViewMoreExpanded
								? selectedLeafPaths
								: selectedLeafPaths.slice(0, 5)
							).map(path => (
								<ListItem key={path} sx={{ py: 0.25, pl: 1 }}>
									<Typography variant="body2">• {path}</Typography>
								</ListItem>
							))}
						</List>
						{selectedLeafPaths.length > 5 && !isViewMoreExpanded && (
							<Button
								size="small"
								startIcon={<ExpandMore />}
								onClick={() => setIsViewMoreExpanded(true)}
								color="primary"
								sx={{ mt: 0.5, pl: 1 }}
							>
								<FormattedMessage id="VIEW_MORE" />
							</Button>
						)}
					</DialogContent>
					<DialogActions sx={{ px: 2, pb: 2 }}>
						<Button
							variant="outlined"
							onClick={handleDeleteCancel}
							disabled={isDeleting}
						>
							<FormattedMessage id="CANCEL" />
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={handleDeleteConfirm}
							disabled={isDeleting}
							startIcon={
								isDeleting ? (
									<CircularProgress size={14} color="inherit" />
								) : null
							}
						>
							<FormattedMessage id="DELETE" />
						</Button>
					</DialogActions>
				</Dialog>
			</MuiThemeProvider>
		</StyledEngineProvider>
	);
};

export default memo(AppContainer);
